import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const HEADER_SECTION_IDS = [
  { id: 'data_date', label: 'Data Date' },
  { id: 'generated_date', label: 'Generated Date' },
  { id: 'project_name', label: 'Project Name' },
  { id: 'summary_heading', label: 'Summary Heading' },
  { id: 'client_logo', label: 'Client Logo' },
] as const;

const APPENDIX_SECTION_IDS = [
  { id: 'calculation_and_logic', label: 'Calculation & Logic' },
] as const;

const FOOTER_SECTION_IDS = [
  { id: 'footer_confidential', label: 'Confidential' },
  { id: 'footer_page_numbers', label: 'Page Numbers' },
] as const;

const TABLE_SECTION_IDS = [
  { id: 'severity_indicators', label: 'Row status indicators' },
] as const;

const LOGO_SCALE_MIN = 50;
const LOGO_SCALE_MAX = 400;
const LOGO_SCALE_STEP = 10;
const LOGO_SCALE_DEFAULT = 100;

export interface ResourceUtilizationTableOptions {
  sort_by: string;
  sort_order: 'asc' | 'desc';
  filter_role: string;
  filter_status: string;
  filter_search: string;
}

export const RESOURCE_UTILIZATION_TABLE_DEFAULTS: ResourceUtilizationTableOptions = {
  sort_by: 'resource_name',
  sort_order: 'asc',
  filter_role: '',
  filter_status: '',
  filter_search: '',
};

export interface PeriodPerformanceOptions {
  period_start: string;
  period_end: string;
}

export const PERIOD_PERFORMANCE_DEFAULTS: PeriodPerformanceOptions = {
  period_start: '',
  period_end: '',
};

export interface MilestoneStatusOptions {
  sort_by: 'planned_finish' | 'actual_finish';
  status_filter: 'all' | 'complete' | 'in_progress' | 'not_started';
}

export const MILESTONE_STATUS_DEFAULTS: MilestoneStatusOptions = {
  sort_by: 'planned_finish',
  status_filter: 'all',
};

export interface LookaheadOptions {
  start_date: string;
  end_date: string;
}

export const LOOKAHEAD_DEFAULTS: LookaheadOptions = {
  start_date: '',
  end_date: '',
};

export interface ReportDisplayOptions {
  sections: Record<string, { visible: boolean; scale_percent?: number }>;
  header?: { show_time?: boolean };
  resource_utilization?: Partial<ResourceUtilizationTableOptions>;
  period_performance?: Partial<PeriodPerformanceOptions>;
  milestone_status?: Partial<MilestoneStatusOptions>;
  lookahead?: Partial<LookaheadOptions>;
  /** High Float / Low Float report: threshold in days (sent to preview/generate) */
  high_float_threshold_days?: string;
  low_float_threshold_days?: string;
  /** High Duration Tasks report: duration threshold in days */
  duration_threshold_days?: string;
}

export interface ReportDisplayOptionsPanelProps {
  templateId: string;
  displayOptions: ReportDisplayOptions;
  onDisplayOptionsChange: (value: ReportDisplayOptions) => void;
  hasClientLogo?: boolean;
  /** Optional: show "Apply" button and pending state (e.g. in preview flow) */
  showApplyButton?: boolean;
  /** Optional: called when user clicks Apply; only used when showApplyButton is true */
  onApply?: () => void;
  /** Optional: whether options differ from applied (for Apply button state) */
  hasPendingChanges?: boolean;
  /** Optional: loading state for Apply button */
  applying?: boolean;
  className?: string;
}

const RESOURCE_UTILIZATION_SORT_OPTIONS = [
  { value: 'resource_name', label: 'Resource' },
  { value: 'role', label: 'Role' },
  { value: 'total_demand_hours', label: 'Demand (hrs)' },
  { value: 'total_capacity_hours', label: 'Capacity (hrs)' },
  { value: 'utilization_pct', label: 'Utilization %' },
  { value: 'total_overload_hours', label: 'Overload (hrs)' },
  { value: 'status', label: 'Status' },
] as const;

const RESOURCE_UTILIZATION_STATUS_OPTIONS = [
  { value: '__all__', label: 'All' },
  { value: 'HEALTHY', label: 'Healthy' },
  { value: 'LOW', label: 'Low' },
  { value: 'OVER', label: 'Over' },
] as const;

export const ReportDisplayOptionsPanel: React.FC<ReportDisplayOptionsPanelProps> = ({
  templateId,
  displayOptions,
  onDisplayOptionsChange,
  hasClientLogo = false,
  showApplyButton = false,
  onApply,
  hasPendingChanges = false,
  applying = false,
  className = '',
}) => {
  const isResourceUtilization = templateId?.toLowerCase().includes('resource-utilization') ?? false;
  const isPeriodPerformance = templateId?.toLowerCase().includes('period-performance') ?? false;
  const isMilestoneStatus = templateId?.toLowerCase().includes('milestone-status') ?? false;
  const isLookahead = templateId?.toLowerCase().includes('lookahead') ?? false;
  const isKpiReport = templateId?.toLowerCase().includes('kpi') ?? false;
  const isHighFloatLowFloat = templateId?.toLowerCase().includes('high-float-low-float') ?? false;
  const isHighDurationTasks = templateId?.toLowerCase().includes('high-duration-tasks') ?? false;
  const ruOpts = { ...RESOURCE_UTILIZATION_TABLE_DEFAULTS, ...displayOptions.resource_utilization };
  const ppOpts = { ...PERIOD_PERFORMANCE_DEFAULTS, ...displayOptions.period_performance };
  const msOpts = { ...MILESTONE_STATUS_DEFAULTS, ...displayOptions.milestone_status };
  const lookaheadOpts = { ...LOOKAHEAD_DEFAULTS, ...displayOptions.lookahead };
  const setResourceUtilization = (next: Partial<ResourceUtilizationTableOptions>) => {
    onDisplayOptionsChange({
      ...displayOptions,
      resource_utilization: { ...ruOpts, ...next },
    });
  };
  const setPeriodPerformance = (next: Partial<PeriodPerformanceOptions>) => {
    onDisplayOptionsChange({
      ...displayOptions,
      period_performance: { ...ppOpts, ...next },
    });
  };
  const setMilestoneStatus = (next: Partial<MilestoneStatusOptions>) => {
    onDisplayOptionsChange({
      ...displayOptions,
      milestone_status: { ...msOpts, ...next },
    });
  };
  const setLookahead = (next: Partial<LookaheadOptions>) => {
    onDisplayOptionsChange({
      ...displayOptions,
      lookahead: { ...lookaheadOpts, ...next },
    });
  };
  const setHighFloatThresholdDays = (value: string) => {
    onDisplayOptionsChange({ ...displayOptions, high_float_threshold_days: value });
  };
  const setLowFloatThresholdDays = (value: string) => {
    onDisplayOptionsChange({ ...displayOptions, low_float_threshold_days: value });
  };
  const setDurationThresholdDays = (value: string) => {
    onDisplayOptionsChange({ ...displayOptions, duration_threshold_days: value });
  };
  const clampScale = (n: number) => Math.min(LOGO_SCALE_MAX, Math.max(LOGO_SCALE_MIN, n));
  const clientLogoScale = displayOptions.sections?.client_logo?.scale_percent ?? LOGO_SCALE_DEFAULT;
  const setClientLogoScale = (value: number) => {
    const scaled = clampScale(value);
    onDisplayOptionsChange({
      ...displayOptions,
      sections: {
        ...displayOptions.sections,
        client_logo: { ...displayOptions.sections?.client_logo, visible: displayOptions.sections?.client_logo?.visible ?? false, scale_percent: scaled },
      },
    });
  };

  const renderSectionCheckbox = (id: string, label: string, disabled?: boolean, helperText?: string) => (
    <div key={id} className="space-y-1">
      <label
        className={`flex items-center gap-3 text-sm text-foreground ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      >
        <Checkbox
          checked={displayOptions.sections?.[id]?.visible === true}
          onCheckedChange={(checked) => {
            if (disabled) return;
            onDisplayOptionsChange({
              ...displayOptions,
              sections: {
                ...displayOptions.sections,
                [id]: { ...displayOptions.sections?.[id], visible: checked === true },
              },
            });
          }}
          disabled={disabled}
        />
        <span>{label}</span>
      </label>
      {helperText && (
        <p className="text-xs text-muted-foreground pl-7">{helperText}</p>
      )}
    </div>
  );

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      <h2 className="text-sm font-medium text-foreground pt-0">Display Options</h2>

      <div className="space-y-1.5">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Header Fields
        </div>
        <div className="space-y-1.5">
          {HEADER_SECTION_IDS.filter(({ id }) => {
            if (id === 'client_logo') return false;
            if (id === 'summary_heading' && isKpiReport) return false;
            return true;
          }).map(({ id, label }) =>
            renderSectionCheckbox(id, label)
          )}
          {renderSectionCheckbox(
            'client_logo',
            'Client Logo',
            !hasClientLogo,
            !hasClientLogo ? 'Upload a client logo in Project Details to enable.' : undefined
          )}
          {hasClientLogo && displayOptions.sections?.client_logo?.visible && (
            <div className="flex items-center gap-2 pl-7">
              <span className="text-xs text-muted-foreground shrink-0">Scale:</span>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setClientLogoScale(clientLogoScale - LOGO_SCALE_STEP)}
                  disabled={clientLogoScale <= LOGO_SCALE_MIN}
                  aria-label="Decrease logo scale"
                >
                  −
                </Button>
                <input
                  type="number"
                  min={LOGO_SCALE_MIN}
                  max={LOGO_SCALE_MAX}
                  step={LOGO_SCALE_STEP}
                  value={clientLogoScale}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v)) setClientLogoScale(v);
                  }}
                  className="h-7 w-14 rounded border border-input bg-background px-2 text-center text-xs tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setClientLogoScale(clientLogoScale + LOGO_SCALE_STEP)}
                  disabled={clientLogoScale >= LOGO_SCALE_MAX}
                  aria-label="Increase logo scale"
                >
                  +
                </Button>
              </div>
              <span className="text-xs text-muted-foreground">%</span>
            </div>
          )}
        </div>
      </div>

      {isHighFloatLowFloat && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Thresholds
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">High float (days)</Label>
              <Input
                type="number"
                min={0}
                step={1}
                placeholder="5"
                value={displayOptions.high_float_threshold_days ?? ''}
                onChange={(e) => setHighFloatThresholdDays(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Low float (days)</Label>
              <Input
                type="number"
                min={0}
                step={1}
                placeholder="5"
                value={displayOptions.low_float_threshold_days ?? ''}
                onChange={(e) => setLowFloatThresholdDays(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            High float: tasks with float ≥ this many days. Low float: tasks with float 1–N days. Default 5 days each (shows more tasks).
          </p>
        </div>
      )}

      {isHighDurationTasks && (
        <div className="space-y-1.5">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Threshold
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Duration (days)</Label>
            <Input
              type="number"
              min={1}
              step={1}
              placeholder="20"
              value={displayOptions.duration_threshold_days ?? ''}
              onChange={(e) => setDurationThresholdDays(e.target.value)}
              className="h-8 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Tasks with duration above this many days are listed. Default 20 days if not set.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Appendix / Details
        </div>
        <div className="space-y-1.5">
          {APPENDIX_SECTION_IDS.map(({ id, label }) => renderSectionCheckbox(id, label))}
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Footer
        </div>
        <div className="space-y-1.5">
          {FOOTER_SECTION_IDS.map(({ id, label }) => renderSectionCheckbox(id, label))}
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Table
        </div>
        <div className="space-y-1.5">
          {TABLE_SECTION_IDS.map(({ id, label }) =>
            renderSectionCheckbox(id, label, false, 'Show coloured bar (pass/warn/fail) on the left of table rows.')
          )}
        </div>
      </div>

      {isResourceUtilization && (
        <div className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Table sort & filter
          </div>
          <div className="space-y-2">
            <div className="space-y-1">
              <Label className="text-xs">Sort by</Label>
              <Select
                value={ruOpts.sort_by}
                onValueChange={(v) => setResourceUtilization({ sort_by: v })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RESOURCE_UTILIZATION_SORT_OPTIONS.map(({ value, label }) => (
                    <SelectItem key={value} value={value} className="text-xs">
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Order</Label>
              <Select
                value={ruOpts.sort_order}
                onValueChange={(v: 'asc' | 'desc') => setResourceUtilization({ sort_order: v })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc" className="text-xs">Ascending</SelectItem>
                  <SelectItem value="desc" className="text-xs">Descending</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Filter by role</Label>
              <Input
                className="h-8 text-xs"
                placeholder="e.g. Labor"
                value={ruOpts.filter_role}
                onChange={(e) => setResourceUtilization({ filter_role: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Filter by status</Label>
              <Select
                value={ruOpts.filter_status && ruOpts.filter_status !== '' ? ruOpts.filter_status : '__all__'}
                onValueChange={(v) => setResourceUtilization({ filter_status: v === '__all__' ? '' : v })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  {RESOURCE_UTILIZATION_STATUS_OPTIONS.map(({ value, label }) => (
                    <SelectItem key={value} value={value} className="text-xs">
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Search resource name</Label>
              <Input
                className="h-8 text-xs"
                placeholder="Search…"
                value={ruOpts.filter_search}
                onChange={(e) => setResourceUtilization({ filter_search: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}

      {isPeriodPerformance && (
        <div className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Period
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Period start</Label>
              <Input
                type="date"
                className="h-8 text-xs"
                value={ppOpts.period_start}
                onChange={(e) => setPeriodPerformance({ period_start: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Period end</Label>
              <Input
                type="date"
                className="h-8 text-xs"
                value={ppOpts.period_end}
                onChange={(e) => setPeriodPerformance({ period_end: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}

      {isLookahead && (
        <div className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Date window
          </div>
          <p className="text-xs text-muted-foreground">Activities starting or finishing in this range. Leave empty for default (today + 2 weeks).</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Window start</Label>
              <Input
                type="date"
                className="h-8 text-xs"
                value={lookaheadOpts.start_date}
                onChange={(e) => setLookahead({ start_date: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Window end</Label>
              <Input
                type="date"
                className="h-8 text-xs"
                value={lookaheadOpts.end_date}
                onChange={(e) => setLookahead({ end_date: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}

      {isMilestoneStatus && (
        <div className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Table sort & filter
          </div>
          <div className="space-y-2">
            <div className="space-y-1">
              <Label className="text-xs">Sort by</Label>
              <Select
                value={msOpts.sort_by}
                onValueChange={(v: 'planned_finish' | 'actual_finish') => setMilestoneStatus({ sort_by: v })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned_finish" className="text-xs">Planned finish</SelectItem>
                  <SelectItem value="actual_finish" className="text-xs">Actual finish</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Filter by status</Label>
              <Select
                value={msOpts.status_filter}
                onValueChange={(v: 'all' | 'complete' | 'in_progress' | 'not_started') => setMilestoneStatus({ status_filter: v })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">All</SelectItem>
                  <SelectItem value="complete" className="text-xs">Complete</SelectItem>
                  <SelectItem value="in_progress" className="text-xs">In Progress</SelectItem>
                  <SelectItem value="not_started" className="text-xs">Not Started</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Timestamp formatting
        </div>
        <label className="flex items-center gap-3 cursor-pointer text-sm text-foreground">
          <Checkbox
            checked={displayOptions.header?.show_time === true}
            onCheckedChange={(checked) => {
              onDisplayOptionsChange({
                ...displayOptions,
                header: { ...displayOptions.header, show_time: checked === true },
              });
            }}
          />
          <span>Add time with date</span>
        </label>
      </div>

      {showApplyButton && (
        <>
          {hasPendingChanges && (
            <p className="text-xs text-amber-600 dark:text-amber-500">Changes not applied</p>
          )}
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onApply}
            disabled={!hasPendingChanges || applying}
            className="w-full"
          >
            {applying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Updating…
              </>
            ) : (
              'Apply to preview'
            )}
          </Button>
        </>
      )}
    </div>
  );
};
